import React from 'react';
import { NavLink as RouterLink } from 'react-router-dom';

import styled from '@emotion/styled';

import {
  ADMINUSER,
  CLEARERUSER,
  DISPATCHERUSER,
  USER,
  WAREHOUSEUSER,
} from '../../routes/routeConstants';

export const NavLink = styled(RouterLink)`
  text-decoration: none;
  color: ${({ theme }) => theme.palette.black};
  font-weight: 500;
  display: flex;
  gap: 0.5rem;

  &.footer {
    color: ${({ theme }) => theme.palette.secondaryTextColor};
    font-weight: 600;
  }

  &.hamburger-menu {
    font-size: 1rem;
    font-weight: 600;
    /* color: ${({ theme }) => theme.palette.secondaryTextColor}; */
    color: black;
  }
`;

export const InternalLink = styled(RouterLink)`
  text-decoration: none;
  color: ${({ theme }) => theme.palette.brandPrimary};
  font-weight: 700;
`;

export const StyledRouterLink = styled(RouterLink)`
  text-decoration: none;
`;

export const StyledBackArrowRouterLink = styled(RouterLink)`
  text-decoration: none;
  display: grid;
  place-content: center;

  &:hover {
    color: ${({ theme }) => theme.palette.primaryButtonColor};
  }

  &.active {
    color: ${({ theme }) => theme.palette.primaryButtonColor};
  }
`;

interface IExternalLink {
  children: React.ReactNode;
  to: string;
  className?: string;
}

export const ExternalLink: React.FC<IExternalLink> = ({
  children,
  to,
  className,
}) => {
  return (
    <AnchorTag
      href={to}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
    >
      {children}
    </AnchorTag>
  );
};

export const AnchorTag = styled.a`
  text-decoration: none;
  font-weight: 500;

  &.footer {
    display: grid;
    grid-template-columns: 1.5rem auto;
    gap: 0.5rem;
    color: ${({ theme }) => theme.palette.secondaryTextColor};
    font-weight: 600;
  }

  /* @media (max-width: 480px) {
    width: 100%;
  } */

  &.header_nav__mobile {
    @media (max-width: 480px) {
      display: None;
    }
  }
`;

export const ButtonLink = styled(RouterLink)`
  text-decoration: none;
  font-weight: 500;
`;

interface INavItem {
  icon: React.ReactNode;
  children: string;
  path: string;
  rightIcon?: React.ReactNode;
}

export const NavBoxItem: React.FC<INavItem> = ({ icon, path, children }) => {
  return (
    <NavigationBoxLink
      className={(navData) => (navData.isActive ? 'active' : '')}
      to={path}
    >
      {icon}
      {children}
    </NavigationBoxLink>
  );
};

export const NavigationBoxLink = styled(RouterLink)`
  text-decoration: none;
  color: ${({ theme }) => theme.palette.primaryButtonColor};
  display: flex;
  gap: 1.25rem;

  padding: 0.81rem 1.31rem;
  border-radius: 0.375rem;
  font-size: 1rem;
  font-weight: 400;
  display: flex;
  align-items: center;

  &:hover {
    /* background-color: #d2dfff28; */
    color: ${({ theme }) => theme.palette.primaryButtonColor};
  }

  &.active {
    background-color: #2858d128;
    color: ${({ theme }) => theme.palette.primaryButtonColor};
  }
`;

export const NavItem: React.FC<INavItem> = ({
  icon,
  path,
  children,
  rightIcon,
}) => {
  return (
    <Container>
      <StyledNavLink
        className={(navData) => (navData.isActive ? 'active' : 'none')}
        to={path}
      >
        {icon}
        {children}
        {rightIcon}
      </StyledNavLink>
      {rightIcon && (
        <NavDropDown className="dropDownContent">
          <DropDownContent>
            <SubNavLink to={`/${USER}/${WAREHOUSEUSER}`}>Warehouser</SubNavLink>
          </DropDownContent>
          <DropDownContent>
            <SubNavLink to={`/${USER}/${CLEARERUSER}`}>Clearer</SubNavLink>
          </DropDownContent>
          <DropDownContent>
            <SubNavLink to={`/${USER}/${DISPATCHERUSER}`}>
              Dispatcher
            </SubNavLink>
          </DropDownContent>
          <DropDownContent>
            <SubNavLink to={`/${USER}/${ADMINUSER}`}>Admin</SubNavLink>
          </DropDownContent>
        </NavDropDown>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  position: relative;

  &:hover > div.dropDownContent {
    opacity: 1;
    pointer-events: auto;
  }
`;

const NavDropDown = styled.div`
  display: flex;
  gap: 0.25rem;
  border: 1px solid ${({ theme }) => theme.palette.brandGrey};
  flex-direction: column;
  position: absolute;
  right: 0;
  bottom: calc(-200% - 46px);
  pointer-events: none;
  overflow: hidden;
  width: fit-content;
  background-color: white;
  box-shadow: 0px 8px 16px -2px rgba(46, 58, 158, 0.05),
    0px 7px 19px -2px rgba(46, 58, 158, 0.11),
    0px 0px 1px 0px rgba(46, 58, 158, 0.25);

  border-radius: 0.3125rem;
  opacity: 0;
`;

const DropDownContent = styled.div`
  display: flex;
  cursor: pointer;

  &:hover {
    background-color: #fef8f5;
  }
`;

const SubNavLink = styled(RouterLink)`
  color: ${({ theme }) => theme.palette.tertiaryText};
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.5rem 1.5rem;
  text-decoration: none;
  display: flex;
  width: 100%;
`;

const StyledNavLink = styled(RouterLink)`
  display: flex;
  gap: 0.5rem;
  padding: 1rem;
  text-decoration: none;
  color: ${({ theme }) => theme.palette.tertiaryText};
  font-size: 0.875rem;
  width: 100%;
  font-weight: 700;
  align-items: center;
  border-bottom: 2px solid transparent;

  @media screen and (max-width: 480px) {
    padding-left: 0;
  }

  &:link {
    color: ${({ theme }) => theme.palette.tertiaryText};
  }

  &:hover {
    color: ${({ theme }) => theme.palette.secondaryTextColor};
  }

  &.active {
    font-weight: bold;
    color: ${({ theme }) => theme.palette.brandPrimary};
    border-bottom: 2px solid ${({ theme }) => theme.palette.brandPrimary};
  }
`;

interface INavItem {
  icon: React.ReactNode;
  children: string;
  path: string;
}
